import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { StatusPill, Detail } from "./shared";
import CoupleEditForm from "./CoupleEditForm";
import { devLog } from "@/lib/log";

const CoupleDetailDrawer = ({ row: listRow, onClose, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  // Hydrate slim list row with the full record. The admin
  // /couple-signups list endpoint now uses COUPLE_LIST_PROJECTION
  // so heavy fields (priorities, anything_else, etc.) only come down
  // when the drawer actually opens.
  const [full, setFull] = useState(listRow);
  useEffect(() => {
    setEditing(false);
    setFull(listRow);
    if (!listRow?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/couple-signups/${listRow.id}`);
        if (!cancelled) setFull(r.data);
      } catch (e) {
        devLog("couple detail fetch failed:", e?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Refetch only when the drawer target changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRow?.id]);
  if (!listRow) return null;
  const row = full || listRow;

  const saveEdits = async (patch) => {
    try {
      const r = await api.patch(`/admin/couple-signups/${row.id}`, patch);
      setFull(r.data);
      onUpdated?.(r.data);
      toast("Couple updated", { duration: 1800 });
      setEditing(false);
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not save", {
        duration: 2400,
      });
    }
  };

  return (
    <div
      role="dialog"
      data-testid="couple-detail-drawer"
      className="fixed inset-0 z-50 flex"
    >
      <div
        className="flex-1 bg-[var(--ld-ink)]/40"
        onClick={onClose}
        aria-hidden
      />
      <div className="w-full max-w-xl bg-[var(--ld-ivory)] overflow-y-auto p-8 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
              Couple signup
            </span>
            <h3 className="font-serif-display text-2xl mt-2 text-[var(--ld-ink)]">
              {row.couple_full_name}
            </h3>
            <div className="mt-2"><StatusPill status={row.status} /></div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] p-1"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>
        {!editing ? (
          <>
            <button
              type="button"
              data-testid="couple-edit-toggle"
              onClick={() => setEditing(true)}
              className="mt-5 inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/30 px-3 py-1.5 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
            >
              Edit details
            </button>
            <dl className="mt-6">
              <Detail label="Email" value={row.email} />
              <Detail label="Mobile" value={row.mobile_number} />
              <Detail label="Wedding date" value={row.wedding_date} />
              <Detail label="Guests" value={row.guest_count} />
              <Detail label="Venue" value={row.venue_address} />
              <Detail label="Restrictions" value={row.venue_restrictions} />
              <Detail label="Socials" value={row.social_handles} />
              <Detail label="Budget" value={row.budget} />
              <Detail label="Wedding type" value={row.wedding_type} />
              <Detail label="Services" value={row.services_needed} />
              <Detail label="Vibe" value={row.overall_vibe} />
              <Detail label="Priorities" value={row.priorities} />
              <Detail label="Must-haves" value={row.must_haves} />
              <Detail label="Communication" value={row.communication_preference} />
              <Detail label="Already booked" value={row.vendors_already_booked} />
              <Detail label="Anything else" value={row.anything_else} />
              <Detail label="Heard via" value={row.heard_about_us} />
              <Detail
                label="Submitted"
                value={
                  row.submitted_at
                    ? new Date(row.submitted_at).toLocaleString()
                    : ""
                }
              />
            </dl>
          </>
        ) : (
          <CoupleEditForm
            row={row}
            onSave={saveEdits}
            onCancel={() => setEditing(false)}
          />
        )}
      </div>
    </div>
  );
};

export default CoupleDetailDrawer;
